import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Moon, Sun, ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "@/lib/constants";
import { useTheme } from "@/lib/ThemeContext";
import { useAuth } from "@/lib/AuthContext";

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { theme, toggle } = useTheme();
  const { user } = useAuth();
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <>
      <motion.header
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled
            ? "backdrop-blur-xl bg-[color:var(--bg)]/70 border-b border-[color:var(--border)]"
            : "bg-transparent"
        }`}
        data-testid="site-nav"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="brand-logo">
            <LogoMark />
            <span className="font-semibold tracking-tight text-[15px]">
              LaneX <span className="text-[color:var(--text-muted)]">Labs</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {NAV_LINKS.map((l) => {
              const active = loc.pathname === l.href;
              return (
                <Link
                  key={l.href}
                  to={l.href}
                  data-testid={l.testId}
                  className={`text-[13px] font-medium link-underline transition-colors ${
                    active ? "text-[color:var(--text)]" : "text-[color:var(--text-muted)] hover:text-[color:var(--text)]"
                  }`}
                >
                  {l.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={toggle}
              className="w-9 h-9 grid place-items-center rounded-full border border-[color:var(--border)] hover:border-[color:var(--border-strong)] transition-colors"
              aria-label="Toggle theme"
              data-testid="theme-toggle"
            >
              {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
            </button>

            {user ? (
              <Link
                to={user.role === "admin" ? "/admin" : "/portal"}
                className="hidden sm:inline-flex items-center gap-1.5 px-4 h-9 rounded-full text-[12.5px] font-medium border border-[color:var(--border-strong)] hover:bg-[color:var(--surface-elev)] transition-colors"
                data-testid="nav-portal"
              >
                {user.role === "admin" ? "Admin" : "Portal"}
                <ArrowUpRight size={13} />
              </Link>
            ) : (
              <Link
                to="/login"
                className="hidden sm:inline-flex items-center px-4 h-9 rounded-full text-[12.5px] font-medium text-[color:var(--text-muted)] hover:text-[color:var(--text)] transition-colors"
                data-testid="nav-login"
              >
                Sign in
              </Link>
            )}

            <Link
              to="/contact"
              className="hidden sm:inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[color:var(--text)] text-[color:var(--bg)] text-[12.5px] font-semibold hover:opacity-90 transition-opacity"
              data-testid="nav-quote"
            >
              Request a Quote
              <ArrowUpRight size={13} />
            </Link>

            <button
              className="lg:hidden w-9 h-9 grid place-items-center rounded-full border border-[color:var(--border)]"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              data-testid="nav-menu-open"
            >
              <Menu size={16} />
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile sheet */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-[color:var(--bg)] lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-6 h-16 border-b border-[color:var(--border)]">
              <div className="flex items-center gap-2.5">
                <LogoMark />
                <span className="font-semibold tracking-tight">LaneX Labs</span>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="w-9 h-9 grid place-items-center rounded-full border border-[color:var(--border)]"
                aria-label="Close menu"
                data-testid="nav-menu-close"
              >
                <X size={16} />
              </button>
            </div>
            <div className="px-6 pt-10 flex flex-col gap-6">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.href}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 + i * 0.05 }}
                >
                  <Link to={l.href} className="text-3xl font-serif" data-testid={`m-${l.testId}`}>
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              <div className="mt-8 flex flex-col gap-3">
                <Link to="/contact" className="h-11 px-5 rounded-full bg-[color:var(--text)] text-[color:var(--bg)] font-semibold grid place-items-center" data-testid="m-nav-quote">
                  Request a Quote
                </Link>
                <Link to="/login" className="h-11 px-5 rounded-full border border-[color:var(--border-strong)] font-semibold grid place-items-center" data-testid="m-nav-login">
                  Sign in
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function LogoMark() {
  return (
    <div className="w-8 h-8 rounded-lg relative overflow-hidden" style={{
      background: "conic-gradient(from 210deg at 50% 50%, #2563EB, #7C3AED, #06B6D4, #2563EB)"
    }}>
      <div className="absolute inset-[3px] rounded-md bg-[color:var(--bg)] grid place-items-center">
        <span className="font-serif italic text-[15px] leading-none">Lx</span>
      </div>
    </div>
  );
}
